/**
 * scroll-under-header.js — scroll something to just below the sticky header, correctly.
 *
 * THE BUG THIS EXISTS TO FIX
 * `element.scrollIntoView({behavior:'smooth'})` computes its destination ONCE, when it is
 * called, and does not re-target if the page reflows during the ~500ms animation. Every
 * scroll on this product page reflows:
 *
 *   - opening an accordion section adds ~850px below the click
 *   - closing the previously open section removes its height, and if that section was
 *     ABOVE the one clicked, everything after it jumps up by that much
 *   - images above the target finish loading mid-animation
 *
 * Measured on the throw pillow page: the document height went 5034 -> 5888 -> 5807 inside
 * 270ms of a single click. The scroll had already been aimed at the 5888 layout, so it
 * overshot and the heading landed 5px ABOVE the viewport, hidden behind the header. That
 * is the "I get scrolled past what I clicked" report.
 *
 * `scroll-margin-top` does not save you here either. It is honored (verified), but it only
 * adjusts the destination `scrollIntoView` already computed from a stale layout.
 *
 * THE FIX
 * Wait two frames so any layout change has been applied, then compute the destination from
 * the element's CURRENT position and scroll there explicitly. Two frames rather than one
 * because the first frame applies the style change and the second lets layout settle.
 *
 * The offset comes from the live `--header-height` that assets/header.js keeps updated with
 * a ResizeObserver, never a hardcoded pixel value. See docs/design-language.md.
 */
(() => {
  const FALLBACK_HEADER = 60;

  const headerOffset = () => {
    const raw = getComputedStyle(document.body).getPropertyValue('--header-height');
    const parsed = parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : FALLBACK_HEADER;
  };

  // The product wizard's nav becomes a bar fixed to the bottom of a phone
  // screen; product-wizard.js publishes its live height (0 when it is not
  // fixed) as --bottom-bar-height, the same way header.js does for the header.
  const bottomBarOffset = () => {
    const parsed = parseInt(getComputedStyle(document.body).getPropertyValue('--bottom-bar-height'), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  };

  /**
   * Scroll so `el`'s top edge sits `gap` pixels below the sticky header.
   *
   * With `nearest`, scroll only as far as it takes to show all of `el`
   * between the header and the fixed bottom bar, and not at all when it
   * already shows: what block:'nearest' would do if it knew about either.
   * Something taller than that band is still aligned to the header.
   * @param {Element|null} el
   * @param {{gap?: number, nearest?: boolean}} [options]
   */
  const scrollUnderHeader = (el, { gap = 16, nearest = false } = {}) => {
    if (!el) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const behavior = reduceMotion ? 'auto' : 'smooth';

    // How far the page has to move; 0 when `el` is already where it belongs.
    const distance = () => {
      const rect = el.getBoundingClientRect();
      const top = headerOffset() + gap;
      if (!nearest) return rect.top - top;
      const bottom = window.innerHeight - bottomBarOffset() - gap;
      if (rect.top < top || rect.height > bottom - top) return rect.top - top;
      if (rect.bottom > bottom) return rect.bottom - bottom;
      return 0;
    };

    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        const move = distance();
        if (nearest && Math.abs(move) < 1) return;
        window.scrollTo({ top: Math.max(0, window.scrollY + move), behavior });

        // Two frames is enough for a layout change we caused, but not for one that lands
        // mid-animation. The wizard's sticky subtotal bar used to change the document
        // height by ~80px as it attached on scroll, which on a phone left the heading just
        // above the viewport even after the fix above; images landing late still can.
        //
        // So check once when the scroll finishes and correct if we are off. Bounded to a
        // single correction, and instant, so it can never loop or fight the user.
        if (reduceMotion) return;
        let done = false;
        const settle = () => {
          if (done) return;
          done = true;
          window.removeEventListener('scrollend', settle);
          const drift = distance();
          if (Math.abs(drift) > 4) window.scrollBy({ top: drift, behavior: 'auto' });
        };
        window.addEventListener('scrollend', settle, { once: true });
        // scrollend is not in every browser yet, so fall back on a timer.
        setTimeout(settle, 800);
      }),
    );
  };

  window.instameScrollUnderHeader = scrollUnderHeader;

  /**
   * "Preview Now" appears at the foot of every benefit row and every info section, and
   * points at #product-content-area. That is the whole <product-wizard>, so following it
   * put the hero image at the top of the screen and pushed the thing the button promised,
   * the size / upload steps, below the fold on a phone.
   *
   * Retarget to the step progress bar. The href is left alone so it still works with no
   * JavaScript, where the native anchor jump to the wizard top is a reasonable fallback.
   */
  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('a[href="#product-content-area"]');
    if (!link) return;
    const target =
      document.querySelector('#product-personalize') || document.querySelector('#product-content-area');
    if (!target) return;
    event.preventDefault();
    scrollUnderHeader(target);
  });
})();
